import { PriorAuthRequirement } from '@bariendo/lib/constants';
import { createReference, MedplumClient } from '@medplum/core';
import { DocumentReference, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import { uploadAttachments } from './attachments';
import { fetchBot } from './services';

/** What Upload Documents and the coverage composer accept (BAR-3075): 10 MB, PDF or image. */
export const MAX_UPLOAD_DOCUMENT_SIZE_BYTES = 10 * 1024 * 1024;

export function isAllowedUploadDocumentType(file: File): boolean {
  return file.type === 'application/pdf' || file.type.startsWith('image/');
}

export type UploadDocumentResponse = {
  documentReferences: Reference<DocumentReference>[];
};

/**
 * Uploads files and creates DocumentReference resources for a patient
 * Uses a bot to ensure proper compartment assignment and access control
 * @param medplum - Medplum client instance
 * @param patient - Patient resource
 * @param practitioner - Practitioner resource (author of the documents)
 * @param files - Array of files to upload
 * @returns Array of created DocumentReference resources
 */
export async function uploadDocumentToPatient(
  medplum: MedplumClient,
  patient: Patient,
  practitioner: Practitioner,
  files: File[],
  options?: {
    visibleToPatient?: boolean;
    /** The prior auth item the files are for (BAR-3080). */
    type?: PriorAuthRequirement;
  }
): Promise<DocumentReference[]> {
  if (files.length === 0) {
    throw new Error('No files provided');
  }

  // Upload files and create Binary resources
  const attachments = await uploadAttachments(medplum, files);

  // Use bot to create DocumentReference resources (ensures proper compartment assignment)
  const result = await fetchBot<UploadDocumentResponse>(medplum, 'upload-document', {
    patient: createReference(patient),
    attachments,
    visibleToPatient: options?.visibleToPatient ?? false,
    ...(options?.type ? { type: options.type } : {}),
  });

  // Read the created DocumentReference resources
  const documentReferences = await Promise.all(
    result.documentReferences.map((ref) => medplum.readReference<DocumentReference>(ref))
  );

  return documentReferences;
}
